import { CollectionRef } from "@/types/collection"
import { InspirationRef, PlaceRef } from "@/types/entity"
import {
  ExploreConfigurationResponse,
  ExploreFilters,
  ExploreLocation,
  ExplorePlaceType,
  ExploreType,
  SocialFilters,
} from "@/types/explore"
import { UserPosition } from "@/types/geolocation"
import type { LocationSeedSource } from "@/types/home-location-seed"
import { Location, LocationSearchHit, PlaceSummary } from "@/types/place"
import { TripRef } from "@/types/trip"
import { BoundingBox } from "@/types/util"

import { BaseApi, BaseApiRequestConfig } from "@/lib/base-api"
import { UserApi } from "./user-api"

const FALLBACK_LAT = 37.4417
const FALLBACK_LONG = -122.159309

export class ExploreApi extends BaseApi {
  async getDebugInfo(queryId?: string): Promise<{ audit_logs?: unknown[] }> {
    return await this.get<{ audit_logs?: unknown[] }>(
      `/api/explore/${queryId}/debug`
    )
  }

  async fetchLocationByLatLong(
    lat: number,
    long: number,
    config?: BaseApiRequestConfig
  ): Promise<LocationSearchHit> {
    return await this.get<LocationSearchHit>(
      `/api/explore/location_by_lat_long?lat=${lat}&long=${long}`,
      config
    )
  }

  async fetchExplorePlacesStream(
    placeType: string,
    location: BoundingBox | PlaceRef,
    searchString?: string,
    limit = 18,
    pageIndex = 1,
    currentPosition?: UserPosition,
    filters?: ExploreFilters,
    socialFilters?: SocialFilters,
    contextRef?: TripRef | InspirationRef | CollectionRef,
    config?: BaseApiRequestConfig
  ) {
    let locationParams = {}

    if (Array.isArray(location)) {
      const [minLat, maxLat, minLong, maxLong] = location
      locationParams = {
        geo_box: true,
        north_lat: maxLat,
        south_lat: minLat,
        east_long: maxLong,
        west_long: minLong,
      }
    } else if (location) {
      locationParams = {
        location_entity_ref: location,
      }
    }

    // for events, merge sub_genre into is_a field
    const processedFilters = { ...filters }
    if (placeType === "event" && filters?.sub_genre?.length) {
      processedFilters.is_a = [...(filters.is_a || []), ...filters.sub_genre]
      delete processedFilters.sub_genre
    }

    return await this.post<{
      stream_id: string
      query_id: string
      place_type: ExploreType | null
      suggested_tab_order?: ExploreType[]
    }>(
      `/api/explore/places/stream`,
      {
        limit: limit,
        page_index: pageIndex,
        place_type: placeType,
        explore_tag: "your_vibes",
        q: searchString,
        ...locationParams,
        ...(currentPosition && {
          exact_lat: currentPosition.latitude,
          exact_long: currentPosition.longitude,
        }),
        with_personalization: !processedFilters?.disable_personalization,
        filters: processedFilters,
        social_filters: socialFilters,
        context_ref: contextRef,
      },
      config
    )
  }

  async fetchUserLocation(): Promise<ExploreLocation> {
    const userApi = new UserApi(this.getAxios())
    const location = (await userApi.fetchUserLocation()).location
    return location || this.fetchLocationByLatLong(FALLBACK_LAT, FALLBACK_LONG)
  }

  /** Device fix or profile home only — never the IP city. */
  async fetchKnownLocation(): Promise<ExploreLocation | undefined> {
    const userApi = new UserApi(this.getAxios())
    return (await userApi.fetchUserLocation({ precise: true })).location
  }

  /** Assistant Home seed: fix, home, then IP city, with which step matched. */
  async fetchHomeSeedLocation(): Promise<{
    location?: ExploreLocation
    source?: LocationSeedSource
    approximateFrom?: string
  }> {
    const userApi = new UserApi(this.getAxios())
    const { location, source, approximate_from } =
      await userApi.fetchUserLocation({ fallback: true })
    return { location, source, approximateFrom: approximate_from }
  }

  async fetchExploreLocation(locationRef?: PlaceRef): Promise<ExploreLocation> {
    if (locationRef) {
      try {
        const { place } = await this.get<{ place: PlaceSummary }>(
          `/api/places/${locationRef}`
        )
        return place as Location
      } catch {
        // fail silently
      }
    }
    return this.fetchUserLocation()
  }

  async fetchExploreConfiguration(
    placeType: ExplorePlaceType,
    config?: BaseApiRequestConfig
  ): Promise<ExploreConfigurationResponse> {
    return await this.get<ExploreConfigurationResponse>(
      `/api/explore/configuration?place_type=${placeType}`,
      config
    )
  }
}
